/**
 * Investor Login Page
 * 
 * Unlisted page at /investor/login
 * Email/password authentication for investor portal access.
 * No "Forgot Password" — contact administrator for access issues.
 */
import { useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Lock, Mail, AlertCircle, TrendingUp } from "lucide-react";

const BRAND = {
  bg: "#080d16",
  card: "#0e1623",
  border: "#1a2540",
  accent: "#3b82f6",
  accentGlow: "rgba(59,130,246,0.15)",
  gold: "#f59e0b",
  green: "#10b981",
  text: "#f1f5f9",
  textMuted: "#94a3b8",
  textDim: "#64748b",
};

export default function InvestorLogin() {
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const loginMutation = trpc.investor.login.useMutation({
    onSuccess: () => {
      setLocation("/investor/dashboard");
    },
    onError: (err) => {
      setError(err.message || "Invalid email or password");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    loginMutation.mutate({ email, password });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: BRAND.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Inter', system-ui, sans-serif",
        padding: "24px",
      }}
    >
      {/* Background gradient */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(ellipse at 50% 0%, rgba(59,130,246,0.08) 0%, transparent 60%)`,
          pointerEvents: "none",
        }}
      />

      <div style={{ width: "100%", maxWidth: 420, position: "relative", zIndex: 1 }}>
        {/* Logo / Brand */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 64,
              height: 64,
              borderRadius: 16,
              background: `linear-gradient(135deg, ${BRAND.accent}, #1d4ed8)`,
              marginBottom: 20,
              boxShadow: `0 0 40px ${BRAND.accentGlow}`,
            }}
          >
            <TrendingUp size={32} color="white" />
          </div>
          <h1
            style={{
              color: BRAND.text,
              fontSize: 26,
              fontWeight: 700,
              margin: "0 0 6px 0",
              letterSpacing: "-0.5px",
            }}
          >
            Unifi Investor Portal
          </h1>
          <p style={{ color: BRAND.textMuted, fontSize: 14, margin: 0 }}>
            Portfolio performance &amp; payment tracking
          </p>
        </div>

        {/* Login Card */}
        <div
          style={{
            background: BRAND.card,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 16,
            padding: 32,
            boxShadow: "0 24px 48px rgba(0,0,0,0.4)",
          }}
        >
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  color: BRAND.textMuted,
                  fontSize: 13,
                  fontWeight: 500,
                  marginBottom: 8,
                  letterSpacing: "0.3px",
                }}
              >
                Email Address
              </label>
              <div style={{ position: "relative" }}>
                <Mail
                  size={16}
                  color={BRAND.textDim}
                  style={{
                    position: "absolute",
                    left: 14,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  style={{
                    width: "100%",
                    background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 10,
                    padding: "12px 14px 12px 40px",
                    color: BRAND.text,
                    fontSize: 14,
                    outline: "none",
                    boxSizing: "border-box",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = BRAND.accent)}
                  onBlur={(e) => (e.target.style.borderColor = BRAND.border)}
                />
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: "block",
                  color: BRAND.textMuted,
                  fontSize: 13,
                  fontWeight: 500,
                  marginBottom: 8,
                  letterSpacing: "0.3px",
                }}
              >
                Password
              </label>
              <div style={{ position: "relative" }}>
                <Lock
                  size={16}
                  color={BRAND.textDim}
                  style={{
                    position: "absolute",
                    left: 14,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{
                    width: "100%",
                    background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 10,
                    padding: "12px 14px 12px 40px",
                    color: BRAND.text,
                    fontSize: 14,
                    outline: "none",
                    boxSizing: "border-box",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = BRAND.accent)}
                  onBlur={(e) => (e.target.style.borderColor = BRAND.border)}
                />
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(239,68,68,0.1)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  borderRadius: 8,
                  padding: "10px 14px",
                  marginBottom: 20,
                }}
              >
                <AlertCircle size={15} color="#ef4444" />
                <span style={{ color: "#ef4444", fontSize: 13 }}>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loginMutation.isPending}
              style={{
                width: "100%",
                background: loginMutation.isPending
                  ? "rgba(59,130,246,0.5)"
                  : `linear-gradient(135deg, ${BRAND.accent}, #1d4ed8)`,
                border: "none",
                borderRadius: 10,
                padding: "13px",
                color: "white",
                fontSize: 15,
                fontWeight: 600,
                cursor: loginMutation.isPending ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                letterSpacing: "0.2px",
              }}
            >
              {loginMutation.isPending ? "Signing in..." : "Sign In to Portal"}
            </button>
          </form>

          {/* Contact admin instead of forgot password */}
          <div
            style={{
              marginTop: 24,
              paddingTop: 20,
              borderTop: `1px solid ${BRAND.border}`,
              textAlign: "center",
            }}
          >
            <p style={{ color: BRAND.textDim, fontSize: 13, margin: 0 }}>
              Access issues?{" "}
              <a
                href="mailto:joe@unifipro.ai"
                style={{ color: BRAND.accent, textDecoration: "none", fontWeight: 500 }}
              >
                Contact your administrator
              </a>
            </p>
          </div>
        </div>

        {/* Security badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            marginTop: 20,
          }}
        >
          <Shield size={13} color={BRAND.textDim} />
          <span style={{ color: BRAND.textDim, fontSize: 12 }}>
            Secured with 256-bit encryption · Private investor portal
          </span>
        </div>
      </div>
    </div>
  );
}
